import { Box, LinearProgress, Stack, ToggleButton, ToggleButtonGroup, Typography } from '@mui/material';
import { ReactElement, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import { CopyTsvButton } from '../../component/copyTsvButton/CopyTsvButton';
import { useMarketTerm } from '../../hooks/useMarketTerm';
import {
  TOTAL_SERIES,
  getManufacturerSummary,
  getProductSummary,
  getProductUtilizationAll,
  getUtilizationSeries,
  utilizationStats,
} from '../../redux/slices/tools/toolsSlice';
import { CardStyled } from '../../styled/components';
import { ManufacturerSummary } from './ManufacturerSummary';
import { ProductSummary } from './ProductSummary';

type View = 'products' | 'manufacturers';

type BreakdownSummaryProps = {
  category: string;
  manufacturerFetching: boolean;
  productFetching: boolean;
  // Pin the card to one view and drop the toggle; the Manufacturers page uses
  // this to show the same table the dashboard does, on its own.
  only?: View;
};

// The card under the equipment table: the same breakdown at product grain
// (default) or rolled up per manufacturer, both narrowed to the category
// selected above. The toggle sits top right so it reads as a view switch on the
// card, not a filter.
export const BreakdownSummary = ({ category, manufacturerFetching, productFetching, only }: BreakdownSummaryProps): ReactElement => {
  const [chosen, setView] = useState<View>('products');
  const view = only ?? chosen;
  const fetching = view === 'products' ? productFetching : manufacturerFetching;

  const products = useSelector(getProductSummary);
  const manufacturers = useSelector(getManufacturerSummary);
  const count = useMemo(() => (view === 'products' ? products(category).length : manufacturers(category).length), [view, products, manufacturers, category]);
  const noun = view === 'products' ? 'product' : 'manufacturer';
  const countLabel = `${count.toLocaleString()} ${noun}${count === 1 ? '' : 's'}`;

  // The visible table as spreadsheet cells, one row per line, sparkline
  // reduced to its Max % and Avg % like the columns beside it.
  const term = useMarketTerm();
  const productSeries = useSelector(getProductUtilizationAll);
  const manufacturerSeries = useSelector(getUtilizationSeries);
  const pct = (value: number) => Math.round(value * 10) / 10;
  const copy = useMemo(() => {
    const rates = [term('Tracked'), term('Not Tracked'), `${term('Tracked')} %`, 'Max %', 'Avg %'];
    if (view === 'products') {
      return {
        header: ['Category', 'Manufacturer', 'Product', 'Items', ...rates],
        rows: products(category).map((row) => {
          const stats = utilizationStats(productSeries[row.productId]);
          return [
            row.category,
            row.manufacturerName,
            row.productName,
            row.items,
            row.tracked,
            row.notTracked,
            pct(row.trackedPct),
            stats ? pct(stats.max) : '',
            stats ? pct(stats.avg) : '',
          ];
        }),
      };
    }
    return {
      header: ['Manufacturer', 'Products', 'Items', ...rates],
      rows: manufacturers(category).map((row) => {
        const stats = utilizationStats(manufacturerSeries.byManufacturer[row.manufacturerId]?.[category]);
        return [
          row.manufacturerName,
          row.products,
          row.items,
          row.tracked,
          row.notTracked,
          pct(row.trackedPct),
          stats ? pct(stats.max) : '',
          stats ? pct(stats.avg) : '',
        ];
      }),
    };
  }, [view, products, manufacturers, category, productSeries, manufacturerSeries, term]);

  return (
    <CardStyled sx={{ mb: 6, p: 6 }}>
      <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 2, mb: 1 }}>
        <Box>
          <Stack direction="row" alignItems="center" spacing={0.5}>
            <Typography variant="h6">
              {view === 'products' ? 'Products' : 'Manufacturers'}
              <Typography component="span" variant="body2" color="textSecondary" sx={{ ml: 1 }}>
                ({count.toLocaleString()})
              </Typography>
            </Typography>
            <CopyTsvButton header={copy.header} rows={copy.rows} label={view === 'products' ? 'Products' : 'Manufacturers'} disabled={fetching} />
          </Stack>
          <Typography variant="body2" color="textSecondary">
            {category === TOTAL_SERIES ? 'All product categories' : `Product category: ${category}`}
          </Typography>
        </Box>
        {!only && (
          <ToggleButtonGroup size="small" exclusive value={view} onChange={(_event, value: View | null) => value && setView(value)}>
            <ToggleButton value="products">Products</ToggleButton>
            <ToggleButton value="manufacturers">Manufacturers</ToggleButton>
          </ToggleButtonGroup>
        )}
      </Box>

      {fetching && <LinearProgress />}

      <Box sx={{ overflowX: 'auto' }}>
        {view === 'products' ? (
          <ProductSummary fetching={productFetching} category={category} />
        ) : (
          <ManufacturerSummary fetching={manufacturerFetching} category={category} />
        )}
      </Box>

      <Typography variant="caption" color="textSecondary" sx={{ display: 'block', textAlign: 'right', mt: 1.5 }}>
        {countLabel}
      </Typography>
    </CardStyled>
  );
};
